import React, { useCallback, useRef, useState } from 'react';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import Button, { ButtonWithLoadingState } from '../../components/Button';
import SettingCard from '../../settings/SettingCard';
import { adminToasts } from '../../toasts/toasts';
import { UIActions } from '../../ui/uiSlice';
import CreditCardInput from './CreditCardInput';
import * as styles from './EditCard.module.scss';

interface EditCardModalProps {
  onClose: () => void;
  updateBilling: (params: { source: string }) => Promise<unknown>;
}

const EditCardModal = ({ onClose, updateBilling }: EditCardModalProps) => {
  const dispatch = useAppDispatch();

  const [card, setCard] =
    useState<stripe.elements.ElementChangeResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const getStripeTokenRef = useRef<
    (() => Promise<stripe.TokenResponse>) | undefined
  >(undefined);

  const onBindStripeTokenRef = useCallback(
    (cb?: () => Promise<stripe.TokenResponse>) => {
      getStripeTokenRef.current = cb;
    },
    [],
  );

  const handleSubmit = () => {
    setIsLoading(true);

    getStripeTokenRef
      .current?.()
      .then(({ token, error }) => {
        if (error || !token) {
          setIsLoading(false);
          return dispatch(
            UIActions.pushToast(adminToasts.paymentSourceNotVerifiableError),
          );
        }
        return updateBilling({ source: token.id });
      })
      .finally(onClose);
  };

  const isDisabled = Boolean(isLoading || !card?.complete || card.error);

  return (
    <SettingCard
      description={
        <>
          <div className={styles.details}>
            Entering a new credit card will replace any previous payment
            methods.
          </div>
          <CreditCardInput
            onBind={onBindStripeTokenRef}
            // @ts-expect-error
            onChange={setCard}
          />
          <div className={styles.buttons}>
            <ButtonWithLoadingState
              variant="primary"
              disabled={isDisabled}
              loading={isLoading}
              onClick={handleSubmit}
            >
              Update
            </ButtonWithLoadingState>
            <Button
              className={styles.cancelButton}
              variant="secondary"
              onClick={onClose}
            >
              Cancel
            </Button>
          </div>
        </>
      }
      title="New payment method"
    />
  );
};

export default EditCardModal;
